import {ChangeDetectionStrategy, Component, OnInit, computed, inject, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {AlertLevel} from '../../enums/alert-level';
import {GlobalAlertService} from '../../_services/global-alert.service';
import {DeviceCredentialHttpService} from './data-access/device-credential-http.service';
import {
  CREDENTIAL_STATE_LABEL, DeviceCredentialSecret, DeviceCredentialState, DeviceCredentialSummary,
  ROTATION_MAX_DAYS, ROTATION_MIN_DAYS, ROTATION_RANGE_MESSAGE, credentialState, describeCredentialError
} from './shared/device-credential.models';
import {CredentialSecretDialogComponent, SecretOrigin} from
    './credential-secret-dialog/credential-secret-dialog.component';
import {RevokeCredentialDialogComponent} from './revoke-credential-dialog/revoke-credential-dialog.component';

/**
 * Managing the reusable client id + password a register signs in with (#469, step 7).
 *
 * ### Where this lives, and why
 *
 * The *Zugangsdaten* tab of the register-onboarding page (`devices/onboarding/credentials`,
 * `DeviceOnboardingComponent`), beside the QR tab, reached from **Geräte** via *Kasse einrichten*. It is gated
 * by the same `PermissionSet` shape as the QR tab because it is gated by the same `[Authorize]` on the server
 * (`DeviceCredentialController`: `PlatformOwner, CompanyOwner, ShardAdmin`). A credential and a QR code are
 * the two ways a till is given an identity; putting them anywhere but next to each other would ask an
 * administrator to know which of two unrelated screens describes the register in front of them. It is
 * **not** a row action on a device, because a credential is per shard and exists before any device does.
 * This component is the tab's body only — the page chrome (back link, heading, tab strip) is the page's.
 *
 * ### The password is the screen's central fact, not a footnote
 *
 * The server stores a PBKDF2 verifier. There is no read that returns a password — not this one, not any — so
 * the table never pretends otherwise: it shows the client id (which is not secret; it is typed into iPads)
 * and the state, and says in words that the password cannot be shown again. The plaintext appears in exactly
 * two responses, issue and rotate, and both are handed straight to
 * {@link CredentialSecretDialogComponent}, which cannot be dismissed by accident.
 *
 * ### A credential names no account (#279)
 *
 * It used to: a credential was anchored to a register-only account, this screen opened with a picker of such
 * accounts (fed by the enrolment endpoint's listing), and a *Konto für Kassen anlegen* dialog answered the venue
 * whose accounts all belonged to people. Since #279 a credential carries only the **company + shard** it was
 * issued for, and a register presenting it receives a token whose roles are exactly `[Register]` — there is
 * nobody to inherit grants from, so there is nothing to pick and nothing to gate. The issue section is a label
 * and one button; the shard is the session's, which the copy says in words.
 *
 * ### "Standort", not "Verkaufspunkt"
 *
 * A credential is scoped to a **shard**, and the word for that on the screens is *Standort*. A *Verkaufspunkt*
 * is a location inside a shard (the device cards use the word for exactly that — "Mainfloor"), so calling the
 * shard by it sent the reader to the wrong screen. Every sentence here, the table column and the phone card's
 * shard line say Standort.
 */
@Component({
  selector: 'app-device-credentials',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, CredentialSecretDialogComponent, RevokeCredentialDialogComponent],
  template: `
    <div class="flex flex-col gap-4">
      <!-- The page chrome (back link, heading, tab strip) belongs to DeviceOnboardingComponent; this is the body
           of its "Zugangsdaten" tab. What a credential IS is said once, in the issue card below. -->
        @if (loading()) {
          <div class="flex items-center justify-center py-20" aria-label="Lädt">
            <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
            </svg>
          </div>
        } @else {

          <section class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6"
                   aria-labelledby="credential-issue-heading">
            <h2 id="credential-issue-heading" class="mb-3 text-base font-bold tracking-tight text-gray-800">
              Neue Zugangsdaten ausstellen
            </h2>

            <!-- Says what the credential IS, so nobody looks for the account picker this section used to
                 open with: it is scoped to the Standort, the till gets Kassenrechte and nothing else (#279). -->
            <p class="mb-4 text-sm text-gray-600">
              Die Zugangsdaten gelten für diesen Standort und für beliebig viele Kassen. Eine Kasse, die sich
              damit anmeldet, erhält ausschließlich Kassenrechte — ein Konto wird dafür nicht benötigt.
            </p>

            <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div class="min-w-0 flex-1 sm:max-w-sm">
                <label for="credential-name"
                       class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Bezeichnung</label>
                <input id="credential-name" type="text" [value]="name()" (input)="onNameInput($event)"
                       placeholder="z. B. Tresen" maxlength="64"
                       class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm
                              text-gray-900 shadow-sm transition-all duration-200
                              focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500">
              </div>
              <button type="button" (click)="issue()" [disabled]="busy()"
                      class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5
                             py-2.5 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100
                             transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl
                             active:scale-95 disabled:cursor-not-allowed disabled:opacity-30
                             disabled:grayscale">
                Zugangsdaten ausstellen
              </button>
            </div>

            <p class="mt-2 text-xs text-gray-500">
              Die Bezeichnung ist nur für Sie — zum Beispiel der Ort der Kasse. Das Passwort wird nach dem
              Ausstellen genau einmal angezeigt.
            </p>
          </section>

          <section class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6"
                   aria-labelledby="credential-rotation-heading">
            <h2 id="credential-rotation-heading" class="mb-3 text-base font-bold tracking-tight text-gray-800">
              Gültigkeit neuer Passwörter
            </h2>

            <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div class="sm:w-56">
                <label for="credential-rotation"
                       class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
                  Gültig für (Tage)
                </label>
                <input id="credential-rotation" type="number" inputmode="numeric"
                       [min]="minDays" [max]="maxDays" [value]="rotationInput()"
                       (input)="onRotationInput($event)"
                       class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm
                              text-gray-900 shadow-sm transition-all duration-200
                              focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500">
              </div>
              <button type="button" (click)="saveRotation()" [disabled]="!rotationChanged() || busy()"
                      class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                             py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                             hover:border-gray-300 hover:bg-gray-50 active:scale-95
                             disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale">
                Intervall speichern
              </button>
            </div>

            <p class="mt-2 text-xs text-gray-500">
              Zwischen {{ minDays }} und {{ maxDays }} Tagen. Die Angabe gilt für das ganze Unternehmen und
              wirkt auf neu ausgestellte und gewechselte Passwörter. Bestehende Zugangsdaten behalten ihr
              Datum.
            </p>
          </section>

          <section class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6"
                   aria-labelledby="credential-list-heading">
            <h2 id="credential-list-heading" class="mb-1 text-base font-bold tracking-tight text-gray-800">
              Ausgestellte Zugangsdaten
            </h2>
            <p class="mb-3 text-xs text-gray-500">
              CheqMe speichert kein Passwort — nur seine Prüfsumme. Ein Passwort lässt sich hier nicht
              nachlesen. Ist es verloren, wechseln Sie es.
            </p>

            @if (!credentials().length) {
              <p class="rounded-2xl border-2 border-dashed border-gray-100 py-6 text-center text-sm italic
                        text-gray-500">
                Für diesen Standort gibt es noch keine Zugangsdaten.
              </p>
            } @else {
              <div class="hidden overflow-x-auto rounded-2xl border border-gray-100 md:block">
                <table class="min-w-full divide-y divide-gray-100 text-sm">
                  <thead class="bg-gray-50">
                    <tr class="text-left text-xs uppercase tracking-wide text-gray-500">
                      <th class="px-4 py-3 font-semibold" scope="col">Bezeichnung</th>
                      <th class="px-4 py-3 font-semibold" scope="col">Kennung</th>
                      @if (showShard()) {
                        <th class="px-4 py-3 font-semibold" scope="col">Standort</th>
                      }
                      <th class="px-4 py-3 font-semibold" scope="col">Ausgestellt</th>
                      <th class="px-4 py-3 font-semibold" scope="col">Gültig bis</th>
                      <th class="px-4 py-3 font-semibold" scope="col">Status</th>
                      <th class="px-4 py-3 text-right font-semibold" scope="col">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    @for (row of credentials(); track row.id) {
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-4 py-3 font-semibold text-gray-900">{{ row.name }}</td>
                        <td class="px-4 py-3 font-mono text-xs text-gray-500">{{ row.clientId }}</td>
                        @if (showShard()) {
                          <td class="px-4 py-3 font-mono text-xs text-gray-500">{{ row.shardId }}</td>
                        }
                        <td class="px-4 py-3 text-gray-600">{{ row.issuedAt | date: 'dd.MM.yyyy' }}</td>
                        <td class="px-4 py-3 text-gray-600">{{ row.expiration | date: 'dd.MM.yyyy' }}</td>
                        <td class="px-4 py-3">
                          <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold
                                       align-top"
                                [class.bg-green-50]="state(row) === 'live'"
                                [class.text-green-700]="state(row) === 'live'"
                                [class.bg-amber-50]="state(row) === 'expired'"
                                [class.text-amber-700]="state(row) === 'expired'"
                                [class.bg-red-50]="state(row) === 'revoked'"
                                [class.text-red-700]="state(row) === 'revoked'">{{ stateLabel(row) }}</span>
                        </td>
                        <!-- Icon buttons, per the style guide's row-action pattern — not the text buttons
                             the phone cards use. With the shard column present the two labelled buttons
                             pushed the Aktionen column past the table's own width: it scrolled, as the
                             pattern requires, but the header and both controls left the viewport, which
                             reads as a broken table rather than as a scrollable one. Each carries an
                             sr-only label and a title, so the action is named for a screen reader and on
                             hover, and the state badge beside it says which actions apply. -->
                        <td class="px-4 py-3">
                          <div class="flex justify-end gap-1">
                            @if (state(row) !== 'revoked') {
                              <button type="button" (click)="rotate(row)" [disabled]="busy()"
                                      title="Passwort wechseln"
                                      class="inline-flex h-8 w-8 items-center justify-center rounded-lg
                                             text-gray-500 transition-colors hover:bg-gray-100
                                             hover:text-gray-700 disabled:cursor-not-allowed
                                             disabled:opacity-30">
                                <span class="sr-only">Passwort wechseln</span>
                                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke-width="2"
                                     stroke="currentColor" aria-hidden="true"
                                     xmlns="http://www.w3.org/2000/svg">
                                  <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M16.023 9.348h4.992V4.356m-4.993 4.992-2.74-2.74a7.5 7.5 0 1 0
                                           1.404 5.14M20.015 9.348 21 8.36"/>
                                </svg>
                              </button>
                              <button type="button" (click)="askRevoke(row)" [disabled]="busy()"
                                      title="Sperren"
                                      class="inline-flex h-8 w-8 items-center justify-center rounded-lg
                                             text-gray-500 transition-colors hover:bg-red-50
                                             hover:text-red-600 disabled:cursor-not-allowed
                                             disabled:opacity-30">
                                <span class="sr-only">Sperren</span>
                                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke-width="2"
                                     stroke="currentColor" aria-hidden="true"
                                     xmlns="http://www.w3.org/2000/svg">
                                  <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1
                                           5.636 5.636m12.728 12.728L5.636 5.636"/>
                                </svg>
                              </button>
                            }
                          </div>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              <ul class="grid grid-cols-1 gap-4 md:hidden" role="list">
                @for (row of credentials(); track row.id) {
                  <li class="rounded-2xl border border-gray-100 p-4 shadow-sm">
                    <div class="flex items-start justify-between gap-3">
                      <p class="min-w-0 text-sm font-bold tracking-tight text-gray-900">{{ row.name }}</p>
                      <span class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs
                                   font-semibold align-top"
                            [class.bg-green-50]="state(row) === 'live'"
                            [class.text-green-700]="state(row) === 'live'"
                            [class.bg-amber-50]="state(row) === 'expired'"
                            [class.text-amber-700]="state(row) === 'expired'"
                            [class.bg-red-50]="state(row) === 'revoked'"
                            [class.text-red-700]="state(row) === 'revoked'">{{ stateLabel(row) }}</span>
                    </div>
                    <p class="mt-1 break-all font-mono text-xs text-gray-500">{{ row.clientId }}</p>
                    @if (showShard()) {
                      <p class="mt-1 break-all font-mono text-xs text-gray-500">
                        Standort {{ row.shardId }}
                      </p>
                    }
                    <dl class="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <dt class="text-gray-500">Ausgestellt</dt>
                        <dd class="font-semibold text-gray-700">{{ row.issuedAt | date: 'dd.MM.yyyy' }}</dd>
                      </div>
                      <div>
                        <dt class="text-gray-500">Gültig bis</dt>
                        <dd class="font-semibold text-gray-700">{{ row.expiration | date: 'dd.MM.yyyy' }}</dd>
                      </div>
                    </dl>
                    @if (state(row) !== 'revoked') {
                      <div class="mt-3 flex flex-col gap-2">
                        <button type="button" (click)="rotate(row)" [disabled]="busy()"
                                class="inline-flex items-center justify-center rounded-2xl border
                                       border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600
                                       shadow-sm transition-all duration-200 hover:border-gray-300
                                       hover:bg-gray-50 active:scale-95 disabled:cursor-not-allowed
                                       disabled:opacity-30">
                          Passwort wechseln
                        </button>
                        <button type="button" (click)="askRevoke(row)" [disabled]="busy()"
                                class="inline-flex items-center justify-center rounded-2xl border
                                       border-red-100 px-4 py-2.5 text-sm font-semibold text-red-700
                                       shadow-sm transition-all duration-200 hover:bg-red-50 active:scale-95
                                       disabled:cursor-not-allowed disabled:opacity-30">
                          Sperren
                        </button>
                      </div>
                    }
                  </li>
                }
              </ul>
            }
          </section>
        }
    </div>

    @if (secret(); as issued) {
      <device-credential-secret-dialog [credential]="issued" [origin]="secretOrigin()"
                                       [name]="secretName()" (acknowledged)="closeSecret()"/>
    }

    @if (pendingRevoke(); as pending) {
      <device-credential-revoke-dialog [credential]="pending" [busy]="busy()"
                                       (confirmed)="confirmRevoke()" (cancelled)="cancelRevoke()"/>
    }
  `
})
export class DeviceCredentialsComponent implements OnInit {
  private readonly http = inject(DeviceCredentialHttpService);
  private readonly alerts = inject(GlobalAlertService);

  protected readonly minDays = ROTATION_MIN_DAYS;
  protected readonly maxDays = ROTATION_MAX_DAYS;

  protected readonly credentials = signal<DeviceCredentialSummary[]>([]);
  /** The label typed for the next credential. Never part of authentication. */
  protected readonly name = signal('');

  /** The stored interval, as the server last confirmed it. `null` until the read answers. */
  protected readonly rotationDays = signal<number | null>(null);
  /** What is in the field, as typed — a string, so an empty field is distinguishable from a zero. */
  protected readonly rotationInput = signal('');

  protected readonly loading = signal(true);
  protected readonly busy = signal(false);

  /** The plaintext just issued or rotated. Held only while its dialog is on screen; never stored. */
  protected readonly secret = signal<DeviceCredentialSecret | null>(null);
  protected readonly secretOrigin = signal<SecretOrigin>('issued');
  protected readonly secretName = signal('');

  /** The single piece of state driving the revoke dialog — one instance, never one per row. */
  protected readonly pendingRevoke = signal<DeviceCredentialSummary | null>(null);

  /**
   * Whether to show the shard column. A platform owner's listing spans the company's whole estate, and
   * without this column two venues' rows would be indistinguishable; for everyone else the listing is one
   * shard and the column would be a repeated constant.
   */
  protected readonly showShard = computed(() =>
    new Set(this.credentials().map(c => c.shardId)).size > 1);

  protected readonly rotationChanged = computed(() =>
    this.rotationInput().trim() !== String(this.rotationDays() ?? ''));

  ngOnInit(): void {
    this.reload(true);

    this.http.rotation().subscribe({
      next: setting => {
        this.rotationDays.set(setting.days);
        this.rotationInput.set(String(setting.days));
      },
      // Non-fatal: the list is the page's subject and stands on its own. The field simply stays empty, and
      // its save button stays disabled, rather than offering to overwrite a value nobody has read.
      error: err => this.fail(err, 'Das Intervall konnte nicht geladen werden.')
    });
  }

  protected state(row: DeviceCredentialSummary): DeviceCredentialState {
    return credentialState(row);
  }

  protected stateLabel(row: DeviceCredentialSummary): string {
    return CREDENTIAL_STATE_LABEL[credentialState(row)];
  }

  protected onNameInput(event: Event): void {
    this.name.set((event.target as HTMLInputElement).value);
  }

  protected onRotationInput(event: Event): void {
    this.rotationInput.set((event.target as HTMLInputElement).value);
  }

  /**
   * Issues a credential for the session's shard, with the typed label.
   *
   * No shard is sent: the server defaults to the caller's own, and since #279 there is no account on this
   * screen a different shard could be read off. A platform owner reading a company-wide listing therefore
   * issues into the shard their session names — the copy above the field says so in words ("für diesen
   * Standort"), and the new row's shard column shows where it landed.
   */
  protected issue(): void {
    if (this.busy()) return;

    const label = this.name().trim();

    this.busy.set(true);
    this.http.issue(label).subscribe({
      next: created => {
        this.secretOrigin.set('issued');
        this.secretName.set(label);
        this.secret.set(created);
        this.name.set('');
        this.busy.set(false);
        this.reload();
      },
      error: err => {
        this.busy.set(false);
        this.fail(err, 'Die Zugangsdaten konnten nicht ausgestellt werden.');
      }
    });
  }

  /** Replaces a credential's password, keeping its client id. The new password is shown once. */
  protected rotate(row: DeviceCredentialSummary): void {
    if (this.busy()) return;

    this.busy.set(true);
    this.http.rotate(row.id).subscribe({
      next: rotated => {
        this.secretOrigin.set('rotated');
        this.secretName.set(row.name);
        this.secret.set(rotated);
        this.busy.set(false);
        this.reload();
      },
      error: err => {
        this.busy.set(false);
        this.fail(err, 'Das Passwort konnte nicht gewechselt werden.');
      }
    });
  }

  protected askRevoke(row: DeviceCredentialSummary): void {
    this.pendingRevoke.set(row);
  }

  protected cancelRevoke(): void {
    this.pendingRevoke.set(null);
  }

  protected confirmRevoke(): void {
    const pending = this.pendingRevoke();
    if (!pending || this.busy()) return;

    this.busy.set(true);
    this.http.revoke(pending.id).subscribe({
      next: () => {
        this.busy.set(false);
        this.pendingRevoke.set(null);
        this.alerts.createAndShow('Gesperrt',
          'Mit dieser Kennung kann sich keine Kasse mehr anmelden.', AlertLevel.success);
        this.reload();
      },
      error: err => {
        this.busy.set(false);
        this.pendingRevoke.set(null);
        this.fail(err, 'Die Zugangsdaten konnten nicht gesperrt werden.');
      }
    });
  }

  protected closeSecret(): void {
    this.secret.set(null);
  }

  /**
   * Saves the company's rotation interval.
   *
   * A value that is not a whole number in range is refused **here**, with the sentence the server's
   * `E_DEVICE_CREDENTIAL_ROTATION_OUT_OF_RANGE` carries, so the two answers cannot word the same rule
   * differently. Anything the field does let through is still decided by the server — the range is its rule,
   * not this screen's.
   */
  protected saveRotation(): void {
    if (this.busy()) return;

    const days = Number(this.rotationInput().trim());
    if (!Number.isInteger(days) || days < ROTATION_MIN_DAYS || days > ROTATION_MAX_DAYS) {
      this.alerts.createAndShow('Fehler', ROTATION_RANGE_MESSAGE, AlertLevel.error, 8000);
      return;
    }

    this.busy.set(true);
    this.http.setRotation(days).subscribe({
      next: setting => {
        this.rotationDays.set(setting.days);
        this.rotationInput.set(String(setting.days));
        this.busy.set(false);
        this.alerts.createAndShow('Gespeichert',
          'Neue Passwörter gelten ' + setting.days + ' Tage.', AlertLevel.success);
      },
      error: err => {
        this.busy.set(false);
        this.fail(err, 'Das Intervall konnte nicht gespeichert werden.');
      }
    });
  }

  private reload(initial = false): void {
    this.http.list().subscribe({
      next: rows => {
        this.credentials.set(rows);
        if (initial) this.loading.set(false);
      },
      error: err => {
        if (initial) this.loading.set(false);
        this.fail(err, 'Die Zugangsdaten konnten nicht geladen werden.');
      }
    });
  }

  /**
   * Surfaces a refusal as a German sentence through the global alert banner.
   *
   * Deliberately not `ErrorBannerComponent`: that renders `Error · {{statusText}}` and discards both `code`
   * and `description`, so a named refusal routed through it would reach the reader as nothing at all. The
   * sentence is mapped from the error **code**; the backend's English `description` is never rendered.
   */
  private fail(err: unknown, fallback: string): void {
    this.alerts.createAndShow('Fehler', describeCredentialError(err, fallback), AlertLevel.error, 8000);
  }
}
